import { Team, TeamMember } from '@repo/domain/team';
import { USER_API_URL } from '@repo/editor/src/api/constants';
import { getAccessToken } from '@repo/editor/src/api/user';
import {
  GenericApiResponse,
  TeamInvite,
  TeamMemberInvite,
} from '@repo/editor/src/types';
import { tryFetchRetry } from '@repo/editor/src/utils/fetchRetry';

type CreateTeamResponse = GenericApiResponse<{
  team: Team;
  token: string;
  invites?: TeamMemberInvite[];
}>;

type InvitesListResponse = GenericApiResponse<{
  results: TeamInvite[];
}>;
type TeamMembersResponse = GenericApiResponse<{
  team: Team;
  members: TeamMember[];
  invites: TeamMemberInvite[];
}>;

type AcceptInviteResponse = GenericApiResponse<{
  token: string;
}>;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createAPI = ({ url }: { url: string }) => {
  const acceptInvite = async (code: string): Promise<AcceptInviteResponse> => {
    return await tryFetchRetry<AcceptInviteResponse>(`${url}/invites/accept`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify({
        code,
      }),
    });
  };

  const listInvites = async (
    accepted?: boolean
  ): Promise<InvitesListResponse> => {
    return await tryFetchRetry<InvitesListResponse>(
      `${url}/invites/index${
        accepted === undefined ? '' : `?accepted=${accepted ? 'true' : 'false'}`
      }`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }
    );
  };

  const createInvites = async (
    emails: string[]
  ): Promise<InvitesListResponse> => {
    return await tryFetchRetry<InvitesListResponse>(`${url}/invites/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify({
        emails,
      }),
    });
  };

  const create = async (
    name: string,
    emails?: string[]
  ): Promise<CreateTeamResponse> => {
    return await tryFetchRetry<CreateTeamResponse>(`${url}/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify({
        name,
        emails,
      }),
    });
  };

  const getTeamMembers = async (slug: string): Promise<TeamMembersResponse> => {
    return await tryFetchRetry<TeamMembersResponse>(
      `${url}/${slug}/members?includeInvites=true`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }
    );
  };

  const removeTeamMember = async (
    teamSlug: string,
    userSlug: string
  ): Promise<GenericApiResponse> => {
    return await tryFetchRetry<GenericApiResponse>(
      `${url}/${teamSlug}/remove`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`,
        },
        body: JSON.stringify({
          slug: userSlug,
        }),
      }
    );
  };

  const deleteTeam = async (slug: string): Promise<GenericApiResponse> => {
    return await tryFetchRetry<GenericApiResponse>(`${url}/${slug}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAccessToken()}`,
      },
    });
  };

  const deleteTeamInvites = async (
    emails: string[]
  ): Promise<GenericApiResponse> => {
    return await tryFetchRetry<GenericApiResponse>(`${url}/invites/delete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify({
        emails,
      }),
    });
  };

  const resendTeamInvites = async (
    emails: string[]
  ): Promise<GenericApiResponse> => {
    return await tryFetchRetry<GenericApiResponse>(`${url}/invites/resend`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify({
        emails,
      }),
    });
  };

  return {
    acceptInvite,
    listInvites,
    createInvites,
    create,
    getTeamMembers,
    removeTeamMember,
    deleteTeam,
    deleteTeamInvites,
    resendTeamInvites,
  };
};

export default createAPI({ url: `${USER_API_URL}/teams` });
